import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import PremiumHeader from '../components/PremiumHeader'

function StatCard({ label, value, tone }: { label: string; value: number; tone: 'blue' | 'amber' | 'green' | 'gray' }) {
  const toneClasses: Record<typeof tone, string> = {
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  }

  return (
    <div className={`rounded-xl border p-4 ${toneClasses[tone]} ${value === 0 ? 'opacity-60' : ''}`}>
      <div className="text-xs uppercase tracking-wide opacity-75">{label}</div>
      <div className="text-3xl font-semibold mt-1">{value}</div>
      {value === 0 && <div className="text-xs mt-1 opacity-80">None right now</div>}
    </div>
  )
}

function WorkflowCard({ title, description, actions, className = '' }: { title: string; description: string; actions: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white border rounded-xl p-4 ${className}`.trim()}>
      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">{title}</h3>
      <p className="text-sm text-gray-600 mt-1">{description}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {actions}
      </div>
    </div>
  )
}

export default function DispatcherPage() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const canUseDemoAction = ['SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'OWNER', 'CARRIER_ADMIN', 'DISPATCHER'].includes(user?.accountType ?? '')
  const { data: loads = [], isLoading: loadsLoading } = useQuery({
    queryKey: ['dispatcher-loads'],
    queryFn: async () => (await api.loads.list()).data,
    refetchInterval: 15000,
  })

  const { data: jobs = [], isLoading: jobsLoading } = useQuery({
    queryKey: ['dispatcher-jobs'],
    queryFn: async () => (await api.jobs.list()).data,
    refetchInterval: 15000,
  })

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['dispatcher-invoices'],
    queryFn: async () => (await api.invoices.list()).data,
    refetchInterval: 20000,
  })

  const { data: readyOrders = [], isLoading: readyOrdersLoading } = useQuery({
    queryKey: ['dispatcher-ready-orders'],
    queryFn: async () => (await api.orders.list({ status: 'READY' })).data,
    refetchInterval: 12000,
  })

  const { data: quoteRequests = [], isLoading: quoteRequestsLoading } = useQuery({
    queryKey: ['dispatcher-quote-requests'],
    queryFn: async () => (await api.quoteRequests.list()).data,
    refetchInterval: 12000,
  })

  const { data: quotePortResponse, isLoading: quotePortLoading } = useQuery({
    queryKey: ['dispatcher-quote-port'],
    queryFn: async () => (await api.quotePort.current()).data.quotePort,
    refetchInterval: 30000,
  })

  // QuotePort is resolved from the authenticated tenant's canonical account,
  // not from whether that tenant happens to have quote-request rows yet.
  const quotePortAccountSlug = quotePortResponse?.accountSlug?.trim() ?? null
  const publicQuoteFormPath = quotePortAccountSlug ? '/q/' + encodeURIComponent(quotePortAccountSlug) : null

  const quickUpdateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Parameters<typeof api.quoteRequests.updateStatus>[1] }) =>
      api.quoteRequests.updateStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatcher-quote-requests'] })
      qc.invalidateQueries({ queryKey: ['quote-requests'] })
    },
  })

  const quickAssign = useMutation({
    mutationFn: ({ id }: { id: string }) => api.quoteRequests.updateAssignment(id, { assignmentState: 'assigned' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatcher-quote-requests'] })
      qc.invalidateQueries({ queryKey: ['quote-requests'] })
    },
  })

  const isLoading = loadsLoading || jobsLoading || invoicesLoading || quoteRequestsLoading || readyOrdersLoading || quotePortLoading

  const postedLoads = loads.filter(l => l.status === 'POSTED').length
  const assignedLoads = loads.filter(l => l.status === 'ASSIGNED').length
  const activeJobs = jobs.filter(j => ['IN_TRANSIT', 'IN_PROGRESS', 'PICKED_UP', 'STARTED'].includes(j.status)).length
  const pendingInvoices = invoices.filter(i => i.status === 'PENDING').length
  const needsAttention = quoteRequests.filter((r) => ['new', 'accepted', 'waiting_on_customer', 'expired'].includes(r.status)).slice(0, 5)
  const myWork = quoteRequests.filter((r) => r.assignmentState === 'assigned' || r.status === 'assigned' || r.status === 'in_progress').slice(0, 5)
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const todayQueue = quoteRequests.filter((r) => r.createdAt && new Date(r.createdAt).getTime() >= startOfToday.getTime()).slice(0, 5)
  const recentActivity = [...quoteRequests]
    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime())
    .slice(0, 8)

  const dispatchQueue = loads
    .filter(l => ['POSTED', 'ASSIGNED'].includes(l.status))
    .slice(0, 8)
  const recentJobs = jobs.slice(0, 6)

  const statusLabel = (status: string) => status.replace(/_/g, ' ')

  const QuoteRequestRow = ({ request }: { request: any }) => (
    <div className="border rounded-lg p-3 hover:border-blue-300 transition">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 truncate">{request.customerName} · {request.pickupZip} → {request.deliveryZip}</div>
          <div className="text-xs text-gray-500 mt-1">{request.customerEmail} · {request.vehicleYear ?? ''} {request.vehicleMake ?? ''} {request.vehicleModel ?? ''}</div>
        </div>
        <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 capitalize">{statusLabel(request.status)}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        <button
          type="button"
          onClick={() => quickAssign.mutate({ id: request.id })}
          disabled={quickAssign.isPending || quickUpdateStatus.isPending}
          className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          Assign
        </button>
        <button
          type="button"
          onClick={() => quickUpdateStatus.mutate({ id: request.id, status: 'in_progress' })}
          disabled={quickAssign.isPending || quickUpdateStatus.isPending}
          className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-blue-800 hover:bg-blue-100 disabled:opacity-60"
        >
          Start
        </button>
        <button
          type="button"
          onClick={() => quickUpdateStatus.mutate({ id: request.id, status: 'waiting_on_customer' })}
          disabled={quickAssign.isPending || quickUpdateStatus.isPending}
          className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800 hover:bg-amber-100 disabled:opacity-60"
        >
          Need Customer
        </button>
        <Link to="/quote-desk" className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50">Open Desk</Link>
      </div>
    </div>
  )

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PremiumHeader
        title="Diamond Dispatch — Command Center"
        subtitle="Quote, post, dispatch, track, and invoice vehicle moves from one workspace."
        eyebrow="Quote it. Build it. Post it. Assign it. Track it. Prove it. Invoice it."
      />

      {isLoading ? (
        <div className="text-sm text-gray-400">Loading dispatcher data…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard label="Posted Loads" value={postedLoads} tone="blue" />
            <StatCard label="Assigned Loads" value={assignedLoads} tone="amber" />
            <StatCard label="Active Jobs" value={activeJobs} tone="green" />
            <StatCard label="Pending Invoices" value={pendingInvoices} tone="gray" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Needs Attention</h2>
                <Link to="/quote-requests" className="text-sm text-blue-700 hover:underline">View inbox</Link>
              </div>
              {needsAttention.length === 0 ? <p className="text-sm text-gray-400">No urgent QuotePort work.</p> : <div className="space-y-2">{needsAttention.map((request) => <QuoteRequestRow key={request.id} request={request} />)}</div>}
            </div>

            <div className="bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">My Work</h2>
                <Link to="/quote-desk" className="text-sm text-blue-700 hover:underline">Open Quote Desk</Link>
              </div>
              {myWork.length === 0 ? <p className="text-sm text-gray-400">No assigned quote requests.</p> : <div className="space-y-2">{myWork.map((request) => <QuoteRequestRow key={request.id} request={request} />)}</div>}
            </div>

            <div className="bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Today</h2>
                <span className="text-xs text-gray-500">{todayQueue.length} new requests</span>
              </div>
              {todayQueue.length === 0 ? <p className="text-sm text-gray-400">No new requests today.</p> : <div className="space-y-2">{todayQueue.map((request) => <QuoteRequestRow key={request.id} request={request} />)}</div>}
            </div>

            <div className="bg-white border rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Recent Activity</h2>
                <span className="text-xs text-gray-500">Live lifecycle feed</span>
              </div>
              {recentActivity.length === 0 ? <p className="text-sm text-gray-400">No quote activity yet.</p> : (
                <div className="space-y-2">
                  {recentActivity.map((request) => (
                    <div key={request.id} className="border rounded-lg p-3">
                      <div className="text-sm font-medium text-gray-900">{request.customerName}</div>
                      <div className="text-xs text-gray-500 mt-1">{request.pickupCity}, {request.pickupState} → {request.deliveryCity}, {request.deliveryState}</div>
                      <div className="text-xs text-gray-600 mt-2 capitalize">{statusLabel(request.status)} · {new Date(request.updatedAt || request.createdAt || Date.now()).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border rounded-xl p-4 lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Active Dispatch Work</h2>
                <Link to="/orders" className="text-sm text-blue-700 hover:underline">View Orders</Link>
              </div>
              {!readyOrders.length ? (
                <p className="text-sm text-gray-400">No active orders are waiting for trip assignment.</p>
              ) : (
                <div className="space-y-2">
                  {readyOrders.slice(0, 8).map((order) => {
                    const needsTrip = !order.postedLoadId && !order.assignedJobId
                    const nextAction = needsTrip ? 'Create Trip' : (order.assignedJobId ? 'Open Job' : 'Assign Carrier / Driver')
                    const nextTo = needsTrip ? '/loads/new' : (order.assignedJobId ? `/jobs/${order.assignedJobId}` : `/loads/${order.postedLoadId}`)
                    return (
                      <div key={order.id} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <Link to={`/orders/${order.id}`} className="text-sm font-medium text-gray-900 hover:underline">{order.orderNumber}</Link>
                            <div className="text-xs text-gray-500 mt-1">{order.customerName ?? '—'} · VINs: {order.vinCount ?? 0}</div>
                            <div className="text-xs text-gray-500">Owner: Dispatcher · Missing: {needsTrip ? 'Trip, carrier, driver, equipment' : (order.assignedJobId ? 'None' : 'Carrier/driver/equipment')}</div>
                          </div>
                          <Link to={nextTo} className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100">{nextAction}</Link>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <WorkflowCard
              title="Intake"
              description="Create quotes, convert orders, and import dealer VIN batches."
              actions={
                <>
                  <Link to="/quotes/new" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Create Quote</Link>
                  {publicQuoteFormPath ? (
                    <Link to={publicQuoteFormPath} className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800 hover:bg-blue-100 font-semibold">Open QuotePort Form</Link>
                  ) : (
                    <span className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">QuotePort unavailable for this account</span>
                  )}
                  <Link to="/quote-desk" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">View Quote Desk</Link>
                  <Link to="/orders" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">View Orders</Link>
                  <Link to="/vin-inventory/import" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Import VINs</Link>
                </>
              }
            />

            <WorkflowCard
              title="Dispatch"
              description="Build loads, post to marketplace, and assign drivers or carriers."
              actions={
                <>
                  <Link to="/dispatch-board" className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800 hover:bg-blue-100 font-semibold">Open Dispatch Board</Link>
                  <Link to="/loads/new" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Post Load</Link>
                </>
              }
            />

            <WorkflowCard
              title="Tracking"
              description="Follow jobs, driver updates, and delivery status from one workspace."
              actions={
                <>
                  <Link to="/driver/jobs" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">View Jobs</Link>
                </>
              }
            />

            <WorkflowCard
              title="Documents"
              description="Dispatch sheets, BOL, POD, and rate confirmation workflows."
              actions={
                <>
                  <span className="text-xs text-gray-500 italic">Available on active loads and jobs</span>
                </>
              }
            />

            <WorkflowCard
              title="Money"
              description="Invoices, accounting, settlements, and internal finance workflows."
              actions={
                <>
                  <Link to="/accounting" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Open Accounting</Link>
                  <Link to="/invoices" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">View Invoices</Link>
                </>
              }
            />

            <WorkflowCard
              title="Network"
              description="Manage carriers, drivers, equipment, and shipping lanes."
              actions={
                <>
                  <Link to="/carriers" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Carriers</Link>
                  <Link to="/carrier/drivers" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Drivers</Link>
                  <Link to="/carrier/equipment" className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Equipment</Link>
                </>
              }
            />
          </div>

          {canUseDemoAction && (
            <WorkflowCard
              title="Trailer Loader Beta"
              description="Preview AI-assisted trailer planning and driver-safe load layouts."
              className="border-green-200 bg-green-50/50"
              actions={
                <>
                  <Link to="/driver/trailer-loader" className="rounded-lg border border-green-300 bg-white px-4 py-2 text-sm text-green-800 hover:bg-green-100 font-semibold">Open Trailer Loader Beta</Link>
                  <span className="text-xs text-green-700 italic">Advanced Preview</span>
                </>
              }
            />
          )}

          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Dispatch Queue</h2>
              <Link to="/loads" className="text-sm text-blue-700 hover:underline">View all</Link>
            </div>

            {dispatchQueue.length === 0 ? (
              <p className="text-sm text-gray-400">No queued loads right now.</p>
            ) : (
              <div className="space-y-2">
                {dispatchQueue.map(load => (
                  <div key={load.id} className="border rounded-lg p-3 hover:border-blue-300 transition">
                    <div className="flex items-center justify-between gap-2">
                      <Link to={`/loads/${load.id}`} className="block min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{load.origin} → {load.destination}</div>
                        <div className="text-xs text-gray-500 mt-1">{load.loadNumber ?? load.id.slice(0, 8)} · {load.vehicles?.length ?? 0} vehicles</div>
                      </Link>
                      <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{load.status}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <Link to={`/loads/${load.id}`} className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-blue-800 hover:bg-blue-100 font-semibold">Open Workflow →</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Job POD Status</h2>
                <p className="text-xs text-gray-400 mt-0.5">Access job details and documents from active deliveries.</p>
              </div>
              <Link to="/driver/jobs" className="text-sm text-blue-700 hover:underline">View jobs</Link>
            </div>

            {recentJobs.length === 0 ? (
              <p className="text-sm text-gray-400">No jobs available yet.</p>
            ) : (
              <div className="space-y-2">
                {recentJobs.map(job => (
                  <div key={job.id} className="border rounded-lg p-3 hover:border-blue-300 transition">
                    <div className="flex items-center justify-between gap-3">
                      <Link to={`/jobs/${job.id}`} className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{job.jobNumber ?? job.id.slice(0, 8)}</div>
                        <div className="text-xs text-gray-500 mt-1">{job.status}</div>
                      </Link>
                      <div className={`text-xs font-semibold ${job.proofOfDeliveryUrl ? 'text-green-700' : 'text-amber-700'}`}>
                        {job.proofOfDeliveryUrl ? 'POD: Uploaded' : 'POD: Missing'}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <Link to={`/jobs/${job.id}/dispatch-sheet`} className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50">Dispatch Sheet</Link>
                      <Link to={`/jobs/${job.id}/bol`} className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50">BOL</Link>
                      <Link to={`/jobs/${job.id}/pod`} className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50">POD</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
